import { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Sector } from 'recharts';
import type { PieSectorDataItem } from 'recharts/types/polar/Pie';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type { CombatPowerResponse } from '../../../types/combat-power.js';
import {
  TARGET_OUTLINE_COLOR,
  CURRENT_TARGET_COLOR,
  PDG_COLOR,
  ACE_COLOR,
  tooltipStyle,
  actualBarColor,
  aggregateMembers,
  BudgetBarChart,
} from './BudgetBarChart.js';
import type { ChartItem } from './BudgetBarChart.js';
import { ACE_SUB_TEAMS } from './AceTeamsChart.js';
import { getCombatScope } from '@cortex/domain-product-innovation-lab-combat-power-scope';

// recharts v3.8 removed activeIndex from Pie's public types but it is consumed
// at runtime by PolarGraphicalItem. This typed wrapper avoids the unsafe
// `as Record<string, unknown>` spread pattern. Verify after any recharts major upgrade.
/** @graph-connects none */
const PieWithActive = Pie as unknown as React.ComponentType<
  React.ComponentProps<typeof Pie> & {
    activeIndex?: number;
  }
>;

/** @graph-connects none */
function OverallLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
      <span className="flex items-center gap-1">
        <span
          className="inline-block w-4 h-3 border border-dashed rounded-sm"
          style={{ borderColor: TARGET_OUTLINE_COLOR }}
        />
        {t('members.combatPower.targetFullLabel')}
      </span>
      <span className="flex items-center gap-1">
        <span
          className="inline-block w-4 h-3 rounded-sm"
          style={{ backgroundColor: CURRENT_TARGET_COLOR }}
        />
        {t('members.combatPower.currentTargetLabel')}
      </span>
    </div>
  );
}

/** @graph-connects none */
const RADIAN = Math.PI / 180;
/** @graph-connects none */
const EXPLODE_OFFSET = 5;

/** @graph-connects none */
function PieShapeRenderer(props: PieSectorDataItem & { isActive?: boolean }) {
  const { isActive, cx = 0, cy = 0, startAngle = 0, endAngle = 0 } = props;
  if (!isActive) {
    return <Sector {...props} />;
  }
  const midAngle = (startAngle + endAngle) / 2;
  const dx = EXPLODE_OFFSET * Math.cos(-midAngle * RADIAN);
  const dy = EXPLODE_OFFSET * Math.sin(-midAngle * RADIAN);
  return (
    <g
      style={{
        transition: 'transform 0.25s ease',
        transform: `translate(${String(dx)}px, ${String(dy)}px)`,
      }}
    >
      <Sector {...props} cx={cx} cy={cy} outerRadius={(props.outerRadius ?? 60) + 4} />
    </g>
  );
}

interface AceTeamBreakdown {
  label: string;
  actual: number;
}

/**
 * 覆いは `body` の直下へ `createPortal` で出し、層は `--z-modal` で決める。共有シェル
 * (`AppShell`) の本文ラッパーは登場アニメーション後も `transform: translateY(0)` を残し、
 * それが `position: fixed` の包含ブロックになる。木の中に置いたままだと覆いは本文の枠に
 * 閉じ込められ、サイドバー（`--z-sticky`=100）の領域まで届かない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力ドーナツチャートの ACE グループをクリックしたときに出す、チーム別実績pt内訳モーダル
 * @graph-connects none
 */
export function AceTeamModal({
  teams,
  onClose,
}: {
  teams: AceTeamBreakdown[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const total = teams.reduce((s, team) => s + team.actual, 0);

  // Close on outside click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    },
    [onClose],
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)]"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="absolute bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg shadow-xl p-3 min-w-[180px] animate-content-reveal"
        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-[var(--color-text-primary)]">
            ACE {t('members.combatPower.actualPtLabel')}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] text-sm leading-none px-1"
          >
            ×
          </button>
        </div>
        {teams.map((team) => (
          <div key={team.label} className="flex justify-between gap-4 text-xs py-0.5">
            <span className="text-[var(--color-text-secondary)]">{team.label}</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {team.actual.toLocaleString()}
              {total > 0 && (
                <span className="text-[var(--color-text-muted)] ml-1">
                  ({((team.actual / total) * 100).toFixed(1)}%)
                </span>
              )}
            </span>
          </div>
        ))}
        <div className="flex justify-between text-xs pt-1 mt-1 border-t border-[var(--color-border)] font-bold">
          <span className="text-[var(--color-text-primary)]">Total</span>
          <span className="text-[var(--color-text-primary)]">{total.toLocaleString()}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** @graph-connects none */
export function OverallChart({
  data,
  memberTeamMap,
  currentMonthIdx,
}: {
  data: CombatPowerResponse;
  memberTeamMap: Map<string, { group: string; team: string }>;
  currentMonthIdx: number;
}) {
  const { t } = useTranslation();
  const currentMonth = data.monthHeaders[currentMonthIdx] ?? '';

  const pdgMembers = data.members.filter((m) => {
    const info = memberTeamMap.get(m.name.toLowerCase());
    return info
      ? getCombatScope({ name: m.name, group: info.group, team: info.team }) === 'pdg'
      : false;
  });
  const aceMembers = data.members.filter((m) => {
    const info = memberTeamMap.get(m.name.toLowerCase());
    return info
      ? getCombatScope({ name: m.name, group: info.group, team: info.team }) === 'ace'
      : false;
  });

  const pdg = aggregateMembers(pdgMembers, currentMonthIdx);
  const ace = aggregateMembers(aceMembers, currentMonthIdx);

  const teams: ChartItem[] = [
    {
      label: 'PDG',
      actual: pdg.actual,
      actualInProgress: pdg.actualInProgress,
      target: pdg.capacity,
      currentTarget: pdg.target,
    },
    {
      label: 'ACE',
      actual: ace.actual,
      actualInProgress: ace.actualInProgress,
      target: ace.capacity,
      currentTarget: ace.target,
    },
  ];

  const pieData = [
    { name: 'PDG', value: pdg.actual, color: PDG_COLOR, fill: PDG_COLOR },
    { name: 'ACE', value: ace.actual, color: ACE_COLOR, fill: ACE_COLOR },
  ];
  const totalActual = pdg.actual + ace.actual;

  const [activePieIdx, setActivePieIdx] = useState<number | undefined>();
  const [showAceModal, setShowAceModal] = useState(false);

  const onPieEnter = useCallback((_: unknown, index: number) => {
    setActivePieIdx(index);
  }, []);
  const onPieLeave = useCallback(() => {
    setActivePieIdx(undefined);
  }, []);

  const onPieClick = useCallback((_: unknown, index: number) => {
    // index 1 = ACE (second entry in pieData)
    if (index === 1) {
      setShowAceModal(true);
    }
  }, []);

  // ACE team breakdown for modal
  const aceTeamBreakdown: AceTeamBreakdown[] = ACE_SUB_TEAMS.map((st) => {
    const mems = data.members.filter((m) => {
      const info = memberTeamMap.get(m.name.toLowerCase());
      return info?.group === 'ACE' && info.team === st.team;
    });
    const agg = aggregateMembers(mems, currentMonthIdx);
    return { label: st.label, actual: agg.actual };
  });

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={`${t('members.combatPower.budgetChart')} (${currentMonth})`}>
        <OverallLegend />
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className="flex gap-6 flex-1 flex-col lg:flex-row">
            {teams.map((item) => (
              <div key={item.label} className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-[var(--color-text-primary)] mb-2 text-center">
                  {item.label}
                </h3>
                <div className="h-48">
                  <BudgetBarChart
                    items={[item]}
                    height={192}
                    barSize={60}
                    showXAxis={false}
                    showLegend={false}
                  />
                </div>
                <div className="text-center text-xs text-[var(--color-text-muted)] mt-1">
                  {t('members.combatPower.progressRate')}:{' '}
                  <span
                    className="font-bold"
                    style={{
                      color: actualBarColor(
                        item.actual + item.actualInProgress,
                        item.currentTarget,
                        item.label,
                      ),
                    }}
                  >
                    {item.currentTarget > 0
                      ? `${(((item.actual + item.actualInProgress) / item.currentTarget) * 100).toFixed(1)}%`
                      : '-'}
                  </span>
                  <span className="ml-2">
                    (
                    {item.actualInProgress > 0
                      ? `${item.actual.toLocaleString()}(${item.actualInProgress.toLocaleString()})`
                      : item.actual.toLocaleString()}{' '}
                    / {item.currentTarget.toLocaleString()})
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="shrink-0 w-[200px]">
            <h3 className="text-sm font-bold text-[var(--color-text-primary)] mb-2 text-center">
              {t('members.combatPower.piDivOverview')}
            </h3>
            <div className="h-40 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <PieWithActive
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={60}
                    dataKey="value"
                    nameKey="name"
                    stroke="none"
                    activeIndex={activePieIdx}
                    shape={PieShapeRenderer}
                    onMouseEnter={onPieEnter as never}
                    onMouseLeave={onPieLeave as never}
                    onClick={onPieClick as never}
                    style={{ cursor: 'pointer' }}
                  />
                  <Tooltip
                    cursor={false}
                    contentStyle={{ ...tooltipStyle, opacity: 1, zIndex: 50 }}
                    wrapperStyle={{ zIndex: 50 }}
                    itemStyle={{ color: 'var(--color-text-primary)' }}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={
                      ((value: number, name: string) => [
                        `${value.toLocaleString()} (${String(totalActual > 0 ? ((value / totalActual) * 100).toFixed(1) : 0)}%)`,
                        name,
                      ]) as never
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Total actual in donut center */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <p className="text-xs text-[var(--color-text-muted)] leading-tight">
                    {t('members.combatPower.actualPtLabel')}
                  </p>
                  <p className="text-sm font-bold text-[var(--color-text-primary)] leading-tight">
                    {totalActual.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: PDG_COLOR }}
                />
                PDG {totalActual > 0 ? `${((pdg.actual / totalActual) * 100).toFixed(1)}%` : ''}
              </span>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: ACE_COLOR }}
                />
                ACE {totalActual > 0 ? `${((ace.actual / totalActual) * 100).toFixed(1)}%` : ''}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
      {showAceModal && (
        <AceTeamModal
          teams={aceTeamBreakdown}
          onClose={() => {
            setShowAceModal(false);
          }}
        />
      )}
    </Card>
  );
}
